import { Card } from 'semantic-ui-react';
import TitleContainer from '../TitleContainer';
import { useTranslation } from 'react-i18next';
import styles from '../styles/Activity.module.scss';
import HseUnitCard from './HseUnitCard';
import { TraceabilityState } from '@components/Activity/types/TraceabilityState';
import AddContactsList from '../AddContactsList';
import AssessorContainer from './AssessorContainer';
import { ActivityInitialDataResponse } from '@components/Activity/types/ActivityInitialDataResponse';
import useDistributionListPreFill from '@behavior/activity/getDistributionListPreFill';
import { useEffect, useState } from 'react';
import { Loading } from 'react-lib/dist/components-es';
import { LocationDTO } from '@behavior/inventories/dto/LocationDTO';
import { Dropdown } from 'semantic-ui-react';
import ButtonWrapper from '@components/common/ButtonWrapper';
import { CREATE_MODE } from '@src/constants';

type Props = {
  initialData?: ActivityInitialDataResponse;
  activityTraceabilities?: any;
  disabled?: boolean;
  locations?: LocationDTO[];
  traceabilityState: TraceabilityState;
  mode: string;
};

const TraceabilityFormContainer = ({
  traceabilityState,
  initialData,
  activityTraceabilities,
  disabled,
  locations,
  mode,
}: Props) => {
  const { t } = useTranslation();
  const {
    hseUnit,
    setHseUnit,
    signatureDate,
    setSignatureDate,
    distributionList,
    setDistributionList,
    assessorList,
    setAssessorList,
  } = traceabilityState;

  const { distributionListPreFillPersons, isLoading } = useDistributionListPreFill(locations || []);
  const [selectedOption, setSelectedOption] = useState('');

  useEffect(() => {
    if (
      !isLoading &&
      distributionListPreFillPersons &&
      distributionList &&
      setDistributionList &&
      mode === CREATE_MODE
    ) {
      const newDistributionList = distributionList;
      distributionListPreFillPersons.forEach(contact => {
        const exists = distributionList.some(person => person.person.id === contact.person.id);
        if (!exists) {
          newDistributionList.push(contact);
        }
      });
      setDistributionList([...newDistributionList]);
    }
  }, [isLoading]);

  const options = Array.from(
    new Set(distributionListPreFillPersons?.map(person => person.role))
  ).map((option, index) => ({ key: index, text: option, value: option }));

  if (isLoading) {
    return <Loading isLoading />;
  }

  const handleAddSO = () => {
    distributionListPreFillPersons
      ?.filter(contact => contact.role === selectedOption)
      .forEach(contact => {
        const exists = distributionList.some(p => p.person.id === contact.person.id);
        if (!exists) {
          distributionList.push(contact);
        }
      });

    setDistributionList([...distributionList]);
    setSelectedOption('');
  };

  const getOptionsComponent = () => {
    return (
      <div className="distributionListDropDownWithBtn">
        <Dropdown
          clearable
          options={options}
          selection
          onChange={(e: Event, { value }: { value: string }) => setSelectedOption(value)}
          placeholder={t(
            'traceability.form.details.page.container.distribution.list.dropdown.placeholder'
          )}
          value={selectedOption}
        />
        <ButtonWrapper
          label="traceability.form.details.page.container.distribution.list.placeholder"
          type="button"
          color="primary"
          onClick={handleAddSO}
        />
      </div>
    );
  };

  return (
    <div className={styles.component}>
      <Card className={styles.card}>
        <TitleContainer header="traceability.form.details.page.container.title" />
        <AssessorContainer
          initialData={initialData}
          assessorList={assessorList ?? activityTraceabilities?.traceabilityAssessor}
          setAssessorList={setAssessorList}
          disabled={disabled}
        />
        <HseUnitCard
          initialData={initialData}
          hseUnit={hseUnit ?? activityTraceabilities?.hseUnit}
          setHseUnit={setHseUnit}
          signatureDate={signatureDate ? signatureDate : activityTraceabilities?.signatureDte}
          setSignatureDate={setSignatureDate}
          disabled={disabled}
        />
        <AddContactsList
          contactList={distributionList ?? activityTraceabilities?.distributionList}
          setContactList={setDistributionList}
          title={t('traceability.form.details.page.container.distribution.list')}
          placeholder={t('traceability.form.details.page.container.distribution.list.placeholder')}
          disabled={disabled}
          optionsContainer={getOptionsComponent()}
        />
      </Card>
    </div>
  );
};

export default TraceabilityFormContainer;
